import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Card, CardBody } from "reactstrap";
import ReactApexChart from "react-apexcharts";

import { Heading } from "components/Heading";
import { LoaderFallbackUI } from "components/LoaderFallbackUI";
import DataTable from "components/DataTable/DataTable";
import { getUserMoods } from "api/api.service";
import { convertDateTime, dateTimeFormat } from "utils/dateFormatUtils";
import typography from "../typography";

const GRAPH_COLORS = { teal: "#06402b", gridBorder: "#f6f6f6" };

// Bounded to the last 90 days by default — a chart answers "what's the trend", not
// "show me every row ever", and an unbounded query doesn't scale for long-lived customers.
const DAYS_WINDOW = 90;

const CHART_OPTIONS = {
    chart: { type: "line", toolbar: { show: false } },
    colors: [GRAPH_COLORS.teal],
    stroke: { curve: "smooth", width: 2 },
    markers: { size: 4 },
    dataLabels: { enabled: false },
    xaxis: {
        type: "category",
        labels: { style: { fontSize: "12px", fontFamily: "Poppins" } },
        axisBorder: { show: false },
        axisTicks: { show: false },
    },
    yaxis: {
        min: 0,
        max: 10,
        title: { text: "Intensity", style: { fontSize: "12px", fontFamily: "Poppins" } },
    },
    grid: { borderColor: GRAPH_COLORS.gridBorder },
};

export default function MoodHistory() {
    const { id } = useParams();
    const [loading, setLoading] = useState(true);
    const [moods, setMoods] = useState([]);

    useEffect(() => {
        setLoading(true);
        const end = new Date();
        const start = new Date();
        start.setDate(start.getDate() - DAYS_WINDOW);

        getUserMoods({
            userId: id,
            start_date: convertDateTime({ date: start, customFormat: dateTimeFormat.apiDateTimeFormat }),
            end_date: convertDateTime({ date: end, customFormat: dateTimeFormat.apiDateTimeFormat }),
            limit: 200,
            column: "createdAt",
            direction: "ASC",
        })
            .then((res) => setMoods(res?.data || []))
            .finally(() => setLoading(false));
    }, [id]);

    if (loading) return <LoaderFallbackUI isLoader={loading} />;

    const chartCategories = moods.map(
        (m) => convertDateTime({ date: m.createdAt, customFormat: "MMM DD" }) || ""
    );
    const chartValues = moods.map((m) => m.intensity ?? 0);

    return (
        <div className="profile-details-con p-4 mt-3">
            <Heading heading={typography.MOOD_HISTORY} />
            {moods.length === 0 ? (
                <p className="data-not-found-typo standard-typo-16-600 mt-3">
                    {typography.NO_MOOD_ENTRIES}
                </p>
            ) : (
                <>
                    <Card className="mt-3">
                        <CardBody>
                            <ReactApexChart
                                type="line"
                                series={[{ name: "Intensity", data: chartValues }]}
                                options={{ ...CHART_OPTIONS, xaxis: { ...CHART_OPTIONS.xaxis, categories: chartCategories } }}
                                height={280}
                            />
                        </CardBody>
                    </Card>
                    <DataTable
                        data={moods}
                        loading={false}
                        config={[
                            {
                                title: typography.DATE,
                                render: (row) => (
                                    <div className="standard-typo-14-400">
                                        {convertDateTime({
                                            date: row.createdAt,
                                            customFormat: dateTimeFormat.monthLetterDateFormat,
                                        }) || "-"}
                                    </div>
                                ),
                            },
                            {
                                title: typography.MOOD_TYPE,
                                render: (row) => (
                                    <div className="standard-typo-14-400">{row.moodType || "-"}</div>
                                ),
                            },
                            {
                                title: typography.DESCRIPTION,
                                render: (row) => (
                                    <div className="standard-typo-14-400">
                                        {row.feelingDescription || "-"}
                                    </div>
                                ),
                            },
                        ]}
                    />
                </>
            )}
        </div>
    );
}
